import AssignmentAttachmentPicker from './AssignmentAttachmentPicker';
import React, { useEffect, useRef, useState } from 'react';
import { Download, Eye, Paperclip, Upload } from '../../../../shared/assets/AppIcons';
import { Button } from '@/shared/ui/button';
import {
  downloadDocumentFileAuthenticated,
  getDocumentPreviewObjectUrl,
  listDocumentsResult,
  uploadDocument,
} from '@/modules/documents/services/documents.service';

export default function AssignmentAttachments({ assignment, currentUser, formLayout = false }) {
  const fileInputRef = useRef(null);
  const uploadPending = useRef(false);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadDocuments() {
      setLoading(true);
      setError('');
      const result = await listDocumentsResult({ assignmentId: assignment.id });
      if (cancelled) return;
      setDocuments(result.ok ? result.data.documents : []);
      setError(result.ok ? '' : result.message || 'Backend documenti non disponibile.');
      setLoading(false);
    }

    loadDocuments();

    return () => {
      cancelled = true;
    };
  }, [assignment.id]);

  async function uploadFiles(files) {
    if (uploadPending.current || !files.length) return;
    uploadPending.current = true;
    setUploading(true); setError('');
    try {
      for (const file of files) {
        const result = await uploadDocument({ file, form: {
          assignmentId: assignment.id, clientId: assignment.clientId || null,
          client: assignment.clientName || 'Incarico senza cliente', uploadedBy: currentUser?.name || 'Utente',
          category: 'Allegato incarico', status: 'in_revisione',
        } });
        if (!result.ok) { setError(result.message || 'Upload allegato non completato.'); break; }
        setDocuments(previous => [result.data.document, ...previous]);
      }
    } catch (cause) { setError(cause.message || 'Upload allegato non completato.'); }
    finally { uploadPending.current = false; setUploading(false); }
  }
  function handleUpload(event) {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    uploadFiles(files);
  }

  async function previewDocument(document) {
    const result = await getDocumentPreviewObjectUrl(document);
    if (!result.ok) {
      setError(result.message || 'Anteprima non disponibile.');
      return;
    }

    window.open(result.data.objectUrl, '_blank', 'noopener,noreferrer');
  }

  async function downloadDocument(document) {
    const result = await downloadDocumentFileAuthenticated(document);
    if (!result.ok) {
      setError(result.message || 'Download non disponibile.');
    }
  }

  return (
    <div className={formLayout ? "party-form__field" : "mt-4 space-y-3 border-t border-[var(--border)] pt-4"}>
      {formLayout && <AssignmentAttachmentPicker files={[]} onChange={uploadFiles} disabled={uploading} />}
      {formLayout && <p className="party-form__help">Gli allegati vengono salvati subito nella scheda task.</p>}
      {!formLayout && <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-[var(--text-primary)]">
          <Paperclip size={16} />
          <span>Allegati ({documents.length})</span>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleUpload}
          aria-label="Carica allegato incarico"
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload size={16} className="mr-1" />
          {uploading ? 'Caricamento...' : 'Carica allegato'}
        </Button>
      </div>}

      {loading ? (
        <p className="text-sm text-[var(--text-secondary)]">Caricamento allegati...</p>
      ) : error ? (
        <p role="alert" className="text-sm wt-inline-error">{error}</p>
      ) : documents.length === 0 ? (
        <p className="text-sm italic text-[var(--text-secondary)]">Nessun allegato ancora</p>
      ) : (
        <div className="space-y-2">
          {documents.map((document) => (
            <div key={document.id} className="flex items-center justify-between gap-3 rounded-lg border border-[var(--border)] bg-[var(--surface-raised)] p-3">
              <div>
                <p className="text-sm font-medium text-[var(--text-primary)]">{document.fileName || document.name}</p>
                <p className="text-xs text-[var(--text-secondary)]">{document.size || '—'}</p>
              </div>
              <div className="flex items-center gap-2">
                {document.mimeType === 'application/pdf' ? (
                  <Button type="button" size="sm" variant="outline" onClick={() => previewDocument(document)}>
                    <Eye size={16} className="mr-1" />
                    Anteprima
                  </Button>
                ) : null}
                <Button type="button" size="sm" variant="outline" onClick={() => downloadDocument(document)}>
                  <Download size={16} className="mr-1" />
                  Download
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
